<template>
  <fieldset
    class="ui-radio-group"
    :class="[`ui-radio-group--${orientation}`, { 'ui-radio-group--error': Boolean(error) }]"
    :disabled="disabled"
    :aria-describedby="message ? messageId : undefined"
  >
    <legend v-if="label">{{ label }} <span v-if="required" aria-hidden="true">*</span></legend>
    <div class="ui-radio-group__items">
      <label v-for="item in items" :key="item.id" class="ui-radio">
        <input
          :checked="modelValue === item.id"
          :data-test-id="`ui-radio-${item.id}`"
          :disabled="disabled || item.disabled"
          :name="name"
          :required="required"
          :value="item.id"
          type="radio"
          @change="$emit('update:modelValue', item.id)"
        >
        <span aria-hidden="true" class="ui-radio__dot" />
        <span>{{ item.label }}</span>
      </label>
    </div>
    <p v-if="message" :id="messageId" :class="{ error: Boolean(error) }">{{ message }}</p>
  </fieldset>
</template>
<script setup lang="ts">
  import { computed, useId } from 'vue'
  export type RadioItem = { disabled?: boolean; id: number | string; label: string }
  const props = defineProps<{
    description?: string
    disabled?: boolean
    error?: string
    id?: string
    items: RadioItem[]
    label?: string
    modelValue?: number | string
    name?: string
    orientation?: 'horizontal' | 'vertical'
    required?: boolean
  }>()
  defineEmits<{ 'update:modelValue': [value: number | string] }>()
  const generatedId = useId()
  const resolvedId = computed(() => props.id ?? `ui-radio-${generatedId}`)
  const messageId = computed(() => `${resolvedId.value}-message`)
  const message = computed(() => props.error || props.description)
</script>
<style scoped lang="scss">
@use '@repo/ui/typography.scss' as typography;

/* Selected, disabled and focus states intentionally override hover. */
/* stylelint-disable no-descending-specificity */

.ui-radio-group {
 border: 0;
 display: flex;
 flex-direction: column;
 gap: 12px;
 margin: 0;
 padding: 0;

}

legend {
 @include typography.font-style('text-sm', 'medium');

 color: var(--color-text-text-secondary-700);
 margin-bottom: 2px;
 padding: 0;

}

legend span {
 color: var(--color-brand-600);

}

.ui-radio-group__items {
 display: flex;
 flex-direction: column;
 gap: 8px;

}

.ui-radio-group--horizontal .ui-radio-group__items {
 flex-flow: row wrap;

}

.ui-radio {
 @include typography.font-style('text-sm', 'regular');

 align-items: center;
 color: var(--color-text-text-secondary-700);
 cursor: pointer;
 display: flex;
 gap: 8px;
 position: relative;

}

input {
 height: 1px;
 opacity: 0;
 position: absolute;
 width: 1px;

}

.ui-radio__dot {
 background: var(--color-foreground-fg-white);
 border: 1px solid var(--color-border-border-primary);
 border-radius: 50%;
 height: 16px;
 transition: all 200ms;
 width: 16px;

}

input:focus-visible + .ui-radio__dot {
 outline: 2px solid var(--color-effects-focus-rings-focus-ring);
 outline-offset: 2px;

}

input:checked + .ui-radio__dot {
 border: 5px solid var(--color-background-bg-brand-solid);

}

input:active:not(:disabled) + .ui-radio__dot {
 border-color: var(--color-background-bg-brand-solid);

}

input:disabled + .ui-radio__dot {
 background: var(--color-foreground-fg-disabled-subtle);
 border-color: var(--color-border-border-primary);

}

input:disabled ~ span {
 color: var(--color-text-text-disabled);
 cursor: default;

}

p {
 @include typography.font-style('text-sm', 'regular');

 color: var(--color-text-text-secondary-700);
 margin: 0;

}

p.error {
 color: var(--color-error-600);

}

.ui-radio-group--error .ui-radio__dot {
 border-color: var(--color-foreground-fg-error-primary);
}

</style>
